/**
 * Analytic sky (SPEC §3.9.3).
 *
 * A Preetham-flavoured approximation rather than the full model: a Rayleigh-ish zenith gradient,
 * a Mie forward-scattering lobe around the sun, and a horizon band that warms and thickens as the
 * sun drops. That is enough to make time of day a tactical variable — a low sun on an easterly
 * ingress genuinely washes the camera out — without a lookup table or a paper's worth of terms.
 *
 * GLSL is kept in .ts files as template literals rather than .glsl files so it goes through the
 * same typecheck, lint and bundling path as everything else, with no loader configuration.
 */

export const SKY_VERTEX = /* glsl */ `
varying vec3 vDirection;

void main() {
  vDirection = normalize(position);
  // The dome is drawn at the far plane and never clipped: w = z pins depth at 1.
  vec4 clip = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  gl_Position = clip.xyww;
}
`;

export const SKY_FRAGMENT = /* glsl */ `
precision highp float;

uniform vec3 uSunDirection;
uniform vec3 uZenithColour;
uniform vec3 uHorizonColour;
uniform vec3 uSunColour;
uniform float uSunIntensity;
uniform float uHazeThickness;

varying vec3 vDirection;

/** Henyey–Greenstein phase function: the forward-scattering glow around the sun. */
float miePhase(float cosTheta, float g) {
  float gg = g * g;
  float denominator = 1.0 + gg - 2.0 * g * cosTheta;
  return (1.0 - gg) / (4.0 * 3.14159265 * pow(max(denominator, 1e-4), 1.5));
}

void main() {
  vec3 direction = normalize(vDirection);
  float elevation = direction.y;
  float cosTheta = dot(direction, uSunDirection);

  // Vertical gradient. The exponent controls how tightly the horizon band hugs the skyline;
  // thicker haze at low sun pushes the band further up, which is what sells dusk.
  float t = pow(clamp(elevation, 0.0, 1.0), 0.42 / max(uHazeThickness, 0.25));
  vec3 sky = mix(uHorizonColour, uZenithColour, t);

  // Below the horizon the dome darkens toward the sea rather than mirroring the sky.
  sky = mix(sky * 0.34, sky, smoothstep(-0.12, 0.02, elevation));

  // Forward scattering, strongest near the sun and along the horizon.
  float mie = miePhase(cosTheta, 0.76);
  sky += uSunColour * mie * uSunIntensity * 0.55;

  // The disc itself. Deliberately soft: a hard-edged sun aliases badly under the camera shake
  // and rolling shutter that arrive at M4.
  float disc = smoothstep(0.9995, 0.99985, cosTheta);
  sky += uSunColour * disc * uSunIntensity * 6.0;

  gl_FragColor = vec4(sky, 1.0);
}
`;
